import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { useCallback } from "react";

import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

import { EnterprisePlusModalComponent } from "./EnterprisePlusModalComponent";
import { EnterprisePlusModalUIKitComponent } from "./EnterprisePlusModalUIKitComponent";

interface EnterprisePlusModalProps {
  onClose?: () => void | Promise<void>;
  source?: string;
}

const useCompassModalProps = (onClose?: () => void | Promise<void>) => {
  const modal = useModal();

  const handleOpenChange = useCallback(
    async (open: boolean) => {
      if (!open) {
        if (onClose) {
          await onClose();
        }
        await modal.hide();
        modal.remove();
      }
    },
    [onClose, modal],
  );

  return { isOpen: modal.visible, onOpenChange: handleOpenChange };
};

// Pattern B: Direct component export for use inside other ModalManager.create() wrappers
// (Used by UpgradePlanModal and AiActionsModal)
export const EnterprisePlusModal = ({
  onClose,
  source,
}: EnterprisePlusModalProps) => {
  const isCircleV4 = useIsCircleV4FeatureEnabled("circle_v4_modals_enabled");
  const { isOpen, onOpenChange } = useCompassModalProps(onClose);

  if (isCircleV4) {
    return (
      <EnterprisePlusModalComponent
        open={isOpen}
        onOpenChange={onOpenChange}
        source={source}
      />
    );
  }

  return (
    <EnterprisePlusModalUIKitComponent onClose={onClose} source={source} />
  );
};

// Pattern A: Hook usage via ModalManager.create()
// (Used by CirclePlusBanner and UpgradeFlow via .show())
const EnterprisePlusModalUIKit = ModalManager.create(
  ({ onClose, source }: EnterprisePlusModalProps) => (
    <EnterprisePlusModalUIKitComponent onClose={onClose} source={source} />
  ),
);

const EnterprisePlusModalCompass = ModalManager.create(
  ({ onClose, source }: EnterprisePlusModalProps) => {
    const { isOpen, onOpenChange } = useCompassModalProps(onClose);

    return (
      <EnterprisePlusModalComponent
        open={isOpen}
        onOpenChange={onOpenChange}
        source={source}
      />
    );
  },
);

export const useEnterprisePlusModal = () => {
  const isCircleV4 = useIsCircleV4FeatureEnabled("circle_v4_modals_enabled");
  const compassModal = useModal(EnterprisePlusModalCompass);
  const uikitModal = useModal(EnterprisePlusModalUIKit);

  if (isCircleV4) {
    return compassModal;
  }
  return uikitModal;
};
